import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import {
  Button,
  FormFeedback,
  FormGroup,
  FormText,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { toast } from 'react-toastify';
import CheckIcon from 'mdi-react/CheckIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import InformationOutlineIcon from 'mdi-react/InformationOutlineIcon';
import Api from '../../../util/api';
import { partial } from '../../../util/translation/translation';
import { formatCurrencyNumber } from '../../../util/functions';
import useConfig from '../../../util/useConfig';
import {
  MIN_GAME_CHARGE,
  gameCharge,
  isAllowedAmount as isAllowedKrAmount,
  isWholeKrPlan,
} from '../amountRules';

const p = partial('goalSponsor');
const s = partial('shared');

// Amounts are billed in whole 5 kr units (see the goal sponsor service)
const BILLING_UNIT_KR = 5;
const SEARCH_DELAY_MS = 300;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const companyName = (company) =>
  company?.name || company?.generalName || company?.customCompanyName || '';

const initials = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');

const CompanyLogo = ({ company }) =>
  company?.images?.companyImage ? (
    <img
      className="add-company-sub__logo"
      src={company.images.companyImage}
      alt=""
    />
  ) : (
    <span className="add-company-sub__logo" aria-hidden="true">
      {initials(companyName(company))}
    </span>
  );

const Section = ({ step, title, children }) => (
  <section className="add-company-sub__section">
    <div className="add-company-sub__section-title">
      <span className="add-company-sub__step">{step}</span>
      {title}
    </div>
    {children}
  </section>
);

/**
 * Adds companies to the plan, paid by invoice: active right away, no card.
 * Optionally charges them for the games already played too.
 * `activeCompanyIds`: companies already supporting the plan (not offered).
 */
const AddCompanySubscriptionModal = ({
  isOpen,
  onClose,
  onAdded,
  planId,
  scoreLabel,
  activeCompanyIds,
}) => {
  const config = useConfig();
  const currency = config.currencyCaps;
  const [plan, setPlan] = useState(null);
  const [games, setGames] = useState([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // Picked companies in order: { company, email }
  const [selected, setSelected] = useState([]);
  const [amount, setAmount] = useState('');
  const [cap, setCap] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [includePastGames, setIncludePastGames] = useState(false);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  // Fresh form each time it opens
  useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setResults([]);
    setSelected([]);
    setCap('');
    setAnonymous(false);
    setIncludePastGames(false);
    setTouched(false);
    Api.goalSponsor.subscriptionPlans
      .getSubscriptionPlanById(planId)
      .then((foundPlan) => {
        setPlan(foundPlan);
        setAmount(String(foundPlan?.amountMin || BILLING_UNIT_KR));
      })
      .catch(() => setPlan(null));
    Api.goalSponsor.games
      .getGames(planId)
      .then((found) =>
        setGames(Array.isArray(found) ? found : found?.data || [])
      )
      .catch(() => setGames([]));
  }, [isOpen, planId]);

  // Companies (by name) as the admin types; the first ones before that
  useEffect(() => {
    if (!isOpen) return undefined;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await Api.companies.getCompanies(
          20,
          1,
          search.trim(),
          'name',
          false
        );
        setResults(data?.data || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search, isOpen]);

  const activeIds = useMemo(
    () => new Set(activeCompanyIds || []),
    [activeCompanyIds]
  );
  const selectedIds = new Set(selected.map((item) => item.company.id));

  const toggleCompany = (company) =>
    setSelected((prev) =>
      prev.some((item) => item.company.id === company.id)
        ? prev.filter((item) => item.company.id !== company.id)
        : [...prev, { company, email: company.email || '' }]
    );
  const setEmailFor = (companyId, email) =>
    setSelected((prev) =>
      prev.map((item) =>
        item.company.id === companyId ? { ...item, email } : item
      )
    );

  // The plan's rules in kr. Plans billed in whole kr: at least the plan's
  // minimum, 0.1, 0.25, 0.5 or whole kr. Earlier plans: a minimum, its steps
  // and whole billing units
  const wholeKr = !!plan && isWholeKrPlan(plan);
  const minimum = wholeKr
    ? plan.amountMin || 0.1
    : Math.max(plan?.amountMin || 0, BILLING_UNIT_KR);
  const step = plan?.amountStep || BILLING_UNIT_KR;
  const isAllowedAmount = (value) =>
    Number.isFinite(value) &&
    value >= minimum &&
    (wholeKr
      ? isAllowedKrAmount(value)
      : value % BILLING_UNIT_KR === 0 && value % step === 0);
  const amountNumber = Number(amount);
  const amountValid = isAllowedAmount(amountNumber);
  const quickAmounts = (plan?.popularAmounts || []).filter(isAllowedAmount);
  const capNumber = cap === '' ? null : Number(cap);
  // Whole kr plans: a max in whole kr, as under 5 kr would never be charged
  const capValid =
    capNumber === null ||
    (Number.isFinite(capNumber) &&
      capNumber >= 0 &&
      (!wholeKr ||
        capNumber === 0 ||
        (Number.isInteger(capNumber) && capNumber >= MIN_GAME_CHARGE)));
  const emailsValid = selected.every((item) => EMAIL.test(item.email.trim()));
  const canSave =
    selected.length > 0 &&
    emailsValid &&
    amountValid &&
    capValid &&
    !!plan &&
    !saving;

  // What the games already played would cost each company (as charged:
  // amount per score, capped per game)
  const playedGames = games.filter((game) => game.goalCount > 0);
  const pastScores = playedGames.reduce((sum, game) => sum + game.goalCount, 0);
  const pastAmountPerCompany = amountValid
    ? playedGames.reduce((sum, game) => {
        if (wholeKr) {
          return sum + gameCharge(amountNumber, game.goalCount, capNumber);
        }
        const uncapped = amountNumber * game.goalCount;
        return sum + (capNumber > 0 ? Math.min(uncapped, capNumber) : uncapped);
      }, 0)
    : 0;
  const chargesPastGames = includePastGames && playedGames.length > 0;
  const amountRule = wholeKr
    ? p('amountRuleWholeKr', { min: minimum, currency })
    : p('amountRule', { min: minimum, step, currency });
  const capHint = wholeKr
    ? p('capPerGameHintWholeKr', { min: MIN_GAME_CHARGE, currency })
    : p('capPerGameHint');

  const submit = async () => {
    setTouched(true);
    if (!canSave) return;
    setSaving(true);
    try {
      const result =
        await Api.goalSponsor.subscriptions.createCompanySubscriptions({
          planId,
          companies: selected.map(({ company, email }) => ({
            companyId: company.id,
            userEmail: email.trim(),
            userName: companyName(company) || undefined,
          })),
          amountPerGoal: Math.round(amountNumber * 100),
          ...(capNumber !== null && {
            capPerGame: Math.round(capNumber * 100),
          }),
          isAnonymous: anonymous,
          includePastGames: chargesPastGames,
        });
      if (result?.created?.length) {
        toast.success(p('companiesAdded', { count: result.created.length }));
      }
      if (result?.skipped?.length) {
        const names = result.skipped
          .map(
            ({ companyId }) =>
              companyName(
                selected.find((item) => item.company.id === companyId)?.company
              ) || companyId
          )
          .join(', ');
        toast.warning(p('companiesSkipped', { names }));
      }
      onAdded?.();
      onClose();
    } catch {
      toast.error(p('addCompanyERROR'));
    } finally {
      setSaving(false);
    }
  };

  const money = (value) => `${formatCurrencyNumber(value)} ${currency}`;

  const submitLabel = () => {
    const count = selected.length;
    if (chargesPastGames) {
      return p('addAndChargePast', { count: Math.max(count, 1) });
    }
    return count > 1 ? p('addCompaniesCount', { count }) : p('addCompany');
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      size="lg"
      className="add-company-sub"
    >
      <ModalHeader toggle={onClose}>{p('addCompanyTitle')}</ModalHeader>
      <ModalBody>
        <div className="add-company-sub__intro">
          <InformationOutlineIcon />
          <span>{p('addCompanyInfo')}</span>
        </div>

        <Section step={1} title={p('companies')}>
          <div className="add-company-sub__search">
            <MagnifyIcon />
            <Input
              bsSize="sm"
              autoFocus
              placeholder={p('searchCompany')}
              aria-label={p('searchCompany')}
              value={search}
              invalid={touched && selected.length === 0}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="add-company-sub__results" role="listbox">
            {results.map((item) => {
              const taken = activeIds.has(item.id);
              const picked = selectedIds.has(item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  role="option"
                  aria-selected={picked}
                  disabled={taken}
                  className={classNames('add-company-sub__result', {
                    'add-company-sub__result--picked': picked,
                  })}
                  onClick={() => toggleCompany(item)}
                >
                  <span
                    className={classNames('add-company-sub__check', {
                      'add-company-sub__check--on': picked,
                    })}
                  >
                    {picked && <CheckIcon />}
                  </span>
                  <CompanyLogo company={item} />
                  <span className="add-company-sub__result-text">
                    {companyName(item)}
                    <small>
                      {taken
                        ? p('alreadySubscribedHint')
                        : item.email || p('noContactEmail')}
                    </small>
                  </span>
                </button>
              );
            })}
            {!results.length && (
              <div className="add-company-sub__empty">
                {searching ? `${p('searching')}…` : p('noCompaniesFound')}
              </div>
            )}
          </div>
          {touched && selected.length === 0 && (
            <div className="invalid-feedback d-block">
              {p('pickCompanyFirst')}
            </div>
          )}

          {selected.length > 0 && (
            <div className="add-company-sub__selected">
              <Label className="mb-0">
                {p('contactEmail')} ·{' '}
                {p('selectedCount', { count: selected.length })}
              </Label>
              <FormText className="mt-0 mb-1">{p('contactEmailInfo')}</FormText>
              {selected.map(({ company, email }) => {
                const invalid = touched && !EMAIL.test(email.trim());
                return (
                  <div
                    key={company.id}
                    className="add-company-sub__selected-row"
                  >
                    <CompanyLogo company={company} />
                    <span
                      className="add-company-sub__selected-name"
                      title={companyName(company)}
                    >
                      {companyName(company)}
                    </span>
                    <div className="flex-fill">
                      <Input
                        type="email"
                        bsSize="sm"
                        value={email}
                        placeholder={p('contactEmailPlaceholder')}
                        invalid={invalid}
                        aria-label={`${p('contactEmail')} – ${companyName(
                          company
                        )}`}
                        onChange={(e) =>
                          setEmailFor(company.id, e.target.value)
                        }
                      />
                      {!email && !touched && (
                        <FormText color="warning" className="mt-0">
                          {p('noContactEmail')}
                        </FormText>
                      )}
                    </div>
                    <button
                      type="button"
                      className="add-company-sub__remove"
                      onClick={() => toggleCompany(company)}
                      aria-label={`${s('remove')} ${companyName(company)}`}
                      title={s('remove')}
                    >
                      <CloseIcon />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        <Section step={2} title={p('supportTitle')}>
          <div className="d-flex flex-wrap" style={{ gap: 16 }}>
            <FormGroup className="mb-0" style={{ flex: '1 1 220px' }}>
              <Label for="goalSponsorAmount">
                {p('amountPerScoreShort', { score: scoreLabel })}
              </Label>
              <InputGroup size="sm">
                <Input
                  id="goalSponsorAmount"
                  type="number"
                  min={minimum}
                  step={wholeKr ? 'any' : step}
                  value={amount}
                  invalid={touched && !amountValid}
                  onChange={(e) => setAmount(e.target.value)}
                />
                <InputGroupAddon addonType="append">
                  <InputGroupText>{currency}</InputGroupText>
                </InputGroupAddon>
                <FormFeedback>{amountRule}</FormFeedback>
              </InputGroup>
              {!(touched && !amountValid) && <FormText>{amountRule}</FormText>}
              {quickAmounts.length > 0 && (
                <div className="add-company-sub__amounts">
                  {quickAmounts.map((value) => (
                    <Button
                      key={value}
                      size="sm"
                      color="primary"
                      outline={amountNumber !== value}
                      aria-pressed={amountNumber === value}
                      onClick={() => setAmount(String(value))}
                    >
                      {money(value)}
                    </Button>
                  ))}
                </div>
              )}
            </FormGroup>
            <FormGroup className="mb-0" style={{ flex: '1 1 220px' }}>
              <Label for="goalSponsorCap">{p('capPerGameShort')}</Label>
              <InputGroup size="sm">
                <Input
                  id="goalSponsorCap"
                  type="number"
                  min={0}
                  step={wholeKr ? 1 : undefined}
                  placeholder={p('noCap')}
                  value={cap}
                  invalid={touched && !capValid}
                  onChange={(e) => setCap(e.target.value)}
                />
                <InputGroupAddon addonType="append">
                  <InputGroupText>{currency}</InputGroupText>
                </InputGroupAddon>
                <FormFeedback>{capHint}</FormFeedback>
              </InputGroup>
              {!(touched && !capValid) && <FormText>{capHint}</FormText>}
            </FormGroup>
          </div>
        </Section>

        <Section step={3} title={p('optionsTitle')}>
          <FormGroup check className="mb-2">
            <Label check>
              <Input
                type="checkbox"
                checked={anonymous}
                onChange={(e) => setAnonymous(e.target.checked)}
              />{' '}
              {p('showAnonymously')}
            </Label>
          </FormGroup>
          <div
            className={classNames('add-company-sub__past', {
              'add-company-sub__past--on': includePastGames,
            })}
          >
            <FormGroup check className="mb-0">
              <Label check>
                <Input
                  type="checkbox"
                  checked={includePastGames}
                  disabled={!playedGames.length}
                  onChange={(e) => setIncludePastGames(e.target.checked)}
                />{' '}
                <b>{p('includePastGames')}</b>
              </Label>
              <FormText className="mt-1">
                {playedGames.length
                  ? p('includePastGamesInfo', {
                      games: playedGames.length,
                      scores: pastScores,
                      score: scoreLabel,
                      amount: formatCurrencyNumber(pastAmountPerCompany),
                      currency,
                    })
                  : p('noPastGames')}
              </FormText>
            </FormGroup>
          </div>
        </Section>
      </ModalBody>
      <ModalFooter>
        <div className="add-company-sub__summary" aria-live="polite">
          {selected.length > 0 && amountValid ? (
            <>
              <b>{p('selectedCount', { count: selected.length })}</b>
              {' · '}
              {p('summaryPerScore', {
                amount: money(amountNumber),
                score: scoreLabel,
              })}
              {capNumber > 0 &&
                ` · ${p('summaryCap', { amount: money(capNumber) })}`}
              {chargesPastGames &&
                ` · ${p('summaryPast', {
                  amount: money(pastAmountPerCompany),
                })}`}
            </>
          ) : (
            p('summaryEmpty')
          )}
        </div>
        <Button size="sm" outline onClick={onClose} disabled={saving}>
          {s('cancel')}
        </Button>
        <Button
          size="sm"
          color="primary"
          onClick={submit}
          disabled={saving || (touched && !canSave)}
        >
          {submitLabel()}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddCompanySubscriptionModal;
